// What the ⌘K palette can jump to: every page in the sidebar, each by its
// own name (a hub's pages too: Todos, Deliverables, Org chart, Runs,
// Schedules), with the hub it sits under as its hint ("Todos — Tasks"),
// each agent and its sections ("Julia · Tasks", "Julia · Background tasks"
// where Tasks are off), each subagent blueprint (its page under
// Subagents), each Settings section, and the ways to start something
// beside their page (Record a meeting, Give a task, New schedule, New
// workflow, New subagent), only where that page is on. The things themselves
// (workflows, runs, projects, connections, deployments) are ./jumpObjects.
// Client-side only — there is no content search (files, mail, memories)
// because no endpoint searches them.

import { Archive, ClipboardPlus, Database, Mic, Settings, type LucideIcon } from "lucide-react";

import { sectionAllowed } from "@/features/settings/sections";
import { blueprintPath } from "@/features/subagents/blueprints/paths";
import { dataSourcesPath, savedResultsPath } from "@/features/workflows/paths";

import type { AgentEntry } from "@/hooks/useAgentDirectory";
import { isBlueprint } from "@/lib/agentKind";

import type { FileStoreKind } from "@/features/settings/sections";

import { CODE_SECTIONS, NAV_ITEMS, SETTINGS_SECTIONS, agentSectionsFor, codeSectionLabel, navRow, settingsSectionsFor, type NavFlags, type NavPage } from "./navModel";

export interface JumpItem {
  id: string;
  group: "Go to" | "Workflows" | "Agents" | "Subagents" | "Projects" | "Connections" | "Deployments" | "Recent" | "Settings";
  label: string;
  /** Second, quieter line ("Assistant", "Settings"). */
  hint?: string;
  to: string;
  /** Opens this workflow's Run dialog where the person is, instead of going to `to`. */
  run?: { slug: string; name: string };
  icon?: LucideIcon;
  /** Agent avatar instead of an icon. */
  agent?: AgentEntry;
  /** Extra words that match but are not shown. */
  keywords?: string;
  /** Only listed once something is typed (agent sections). */
  searchOnly?: boolean;
}

/**
 * `flags`: the workspace's settings flags, which gate pages the same way as
 * the sidebar. `withoutDesktop`: templates whose desktop is not exposed (no
 * VNC port — family agents), so no "<Agent> · Desktop" item leads to a
 * missing tab. `briefOf`: a subagent's brief, its hint in the list.
 * `store`: where the workspace keeps its files (capabilities `file_store`;
 * "drive" when not given): on Corvus storage no agent has a Mail section
 * (no inbox of its own) and Settings lists Linked account instead of Mail.
 */
export function buildJumpItems(
  agents: AgentEntry[],
  flags: NavFlags,
  withoutDesktop: ReadonlySet<string>,
  briefOf?: (template: string) => string | null | undefined,
  store: FileStoreKind = "drive",
): JumpItem[] {
  // With Connections on there is one list (Settings › Connections): the data sources are in it, and
  // "connected sessions" are the paired ones, so "connection" means one thing.
  const connections = flags?.connections_enabled === true;
  const pages: JumpItem[] = NAV_ITEMS.flatMap((item) => {
    // The pages that are on, as the sidebar shows them; a page of a hub
    // names the hub ("Todos — Tasks"), unless the hub is down to that page.
    const row = navRow(item, flags);
    if (!row) return [];
    const hub = row.pages.length > 1 ? row.label : undefined;
    return row.pages.flatMap((p) => pageItems(p, hub && hub !== p.label ? hub : undefined, hub, connections));
  });

  // Subagent blueprints have no workspace sections to jump to.
  const people: JumpItem[] = agents.filter((a) => !isBlueprint(a)).flatMap((a) => [
    {
      id: `agent:${a.template}`,
      group: "Agents" as const,
      label: a.name,
      hint: a.role ?? undefined,
      to: `/agents/${a.template}/chat`,
      agent: a,
      keywords: "chat",
    },
    // The agent itself opens Chat, so Chat is not repeated as a section.
    ...agentSectionsFor(!withoutDesktop.has(a.template) && a.kind !== "family", {
      family: a.kind === "family",
      tasksOn: flags?.tasks_enabled === true,
      mail: store !== "corvus",
    })
      .filter((s) => s.key !== "chat")
      .map((s) => ({
        id: `agent:${a.template}:${s.key}`,
        group: "Agents" as const,
        label: `${a.name} · ${s.label}`,
        to: `/agents/${a.template}/${s.key}`,
        icon: s.icon,
        keywords: s.keywords,
        searchOnly: true,
      })),
  ]);

  // A subagent has one home, its page under Subagents (listed whatever
  // the workspace switch says: the page serves subagents that exist).
  // Its hint is its brief (the group already says "Subagents").
  const blueprints: JumpItem[] = agents.filter(isBlueprint).map((a) => ({
    id: `blueprint:${a.template}`,
    group: "Subagents" as const,
    label: a.name,
    hint: briefHint(briefOf?.(a.template)) ?? "Subagent",
    to: blueprintPath("/subagents", a.template),
    agent: a,
    keywords: "subagent",
  }));

  // A section another one replaces (Integrations under Connections) is listed only while that switch is off.
  const settings: JumpItem[] = (store === "corvus" ? settingsSectionsFor(store) : SETTINGS_SECTIONS).filter((s) => sectionAllowed(s, flags)).map((s) => ({
    id: `settings:${s.key}`,
    group: "Settings" as const,
    label: s.label,
    hint: "Settings",
    to: `/settings#${s.key}`,
    icon: Settings,
    keywords: connections && s.key === "connections" ? `${s.keywords} ${DATA_SOURCES_KEYWORDS} data sources` : s.keywords,
  }));

  return [...pages, ...people, ...blueprints, ...settings];
}

// A page's own item, and the items that live with it: Code's sections,
// Data sources and New workflow beside Workflows, Record beside Meetings,
// Give a task beside Tasks (listed without typing), New schedule beside
// Schedules (opens the form at Repeats).
/** What finds the data sources: their own entry where Connections is off, Settings › Connections where it is on. */
const DATA_SOURCES_KEYWORDS = "database db sql postgres connect";

function pageItems(p: NavPage, hint: string | undefined, hub: string | undefined, connections: boolean): JumpItem[] {
  const own: JumpItem = { id: `nav:${p.key}`, group: "Go to", label: p.label, hint, to: p.to, icon: p.icon, keywords: p.keywords };
  if (p.key === "code") {
    return CODE_SECTIONS.map((c) => ({
      id: `code:${c.key}`,
      group: "Go to" as const,
      // "Code" itself opens Coding sessions; the other two say where
      // they live.
      label: c.key === "sessions" ? "Code" : codeSectionLabel(c.key, c.label, connections),
      hint: c.key === "sessions" ? c.label : "Code",
      to: c.to,
      icon: p.icon,
      // Apps: what agents build (dashboards, tools). Not "deployments": that word is
      // Workflows › Deployments (workflows as APIs) now, so it finds only that page.
      keywords: c.key === "apps" ? "code apps dashboards tools" : "code coding",
    }));
  }
  if (p.key === "workflows") {
    const where = hub ?? p.label;
    return [
      own,
      ...(connections
        ? []
        : [{ id: "nav:data-sources", group: "Go to" as const, label: "Data sources", hint: where, to: dataSourcesPath("/workflows"), icon: Database, keywords: DATA_SOURCES_KEYWORDS }]),
      { id: "nav:saved-results", group: "Go to" as const, label: "Saved results", hint: where, to: savedResultsPath("/workflows"), icon: Archive, keywords: "artifact artifacts named result version", searchOnly: true },
      { id: "act:new-workflow", group: "Go to" as const, label: "New workflow", hint: where, to: "/workflows/new", icon: p.icon, keywords: "pipeline template stages", searchOnly: true },
    ];
  }
  if (p.key === "meetings") {
    return [own, { id: "act:record", group: "Go to" as const, label: "Record a meeting", to: "/record", icon: Mic, keywords: "audio transcribe" }];
  }
  if (p.key === "tasks") {
    return [own, { id: "act:give-task", group: "Go to" as const, label: "Give a task", to: "/tasks?give=new", icon: ClipboardPlus, keywords: "new start delegate assign ask team task" }];
  }
  if (p.key === "subagents") {
    return [own, { id: "act:new-subagent", group: "Go to" as const, label: "New subagent", to: "/subagents/new", icon: p.icon, keywords: "create agent design" }];
  }
  if (p.key === "schedules") {
    return [
      own,
      { id: "act:new-schedule", group: "Go to" as const, label: "New schedule", hint: hub ?? p.label, to: "/schedules?id=new", icon: p.icon, keywords: "routine reminder timetable recurring", searchOnly: true },
    ];
  }
  return [own];
}

// The brief's first sentence, cut to fit one line of the palette.
function briefHint(brief: string | null | undefined): string | null {
  const first = (brief ?? "").trim().split(/(?<=[.!?])\s|\n/)[0]?.trim() ?? "";
  if (!first) return null;
  return first.length > 80 ? `${first.slice(0, 80).replace(/\s+\S*$/, "")}…` : first;
}

const WORD_BREAK = /[\s·/,-]+/;

/**
 * Items matching `query`: every word typed must start a word of the label,
 * hint or keywords ("ju ch" finds "Julia · Chat"). Label-start matches
 * first. An empty query lists everything except the search-only items.
 * Both sides split the same way, so "one-off" finds what "one off" does.
 */
export function filterJumpItems(items: JumpItem[], query: string): JumpItem[] {
  const words = query.toLowerCase().split(WORD_BREAK).filter(Boolean);
  if (words.length === 0) return items.filter((i) => !i.searchOnly);
  const scored: Array<[JumpItem, number]> = [];
  for (const item of items) {
    const label = item.label.toLowerCase();
    const hay = `${label} ${item.hint ?? ""} ${item.keywords ?? ""}`.toLowerCase();
    const starts = hay.split(WORD_BREAK).filter(Boolean);
    if (!words.every((w) => starts.some((s) => s.startsWith(w)))) continue;
    const score = (label.startsWith(words[0]) ? 0 : 2) + (item.searchOnly ? 1 : 0);
    scored.push([item, score]);
  }
  return scored.sort((a, b) => a[1] - b[1]).map(([item]) => item);
}
